/**
 * Authentication Hook - Centralizes all authentication logic
 *
 * This hook handles:
 * - Login with email/code
 * - Login with OAuth (Google)
 * - Logout
 * - User data storage in localStorage
 * - Authentication state
 */

import { useState, useCallback } from 'react';
import CryptoJS from 'crypto-js';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be/rest/cabaza';

/**
 * Parse WDDX response from ColdFusion (fallback for non-JSON responses)
 */
const parseWDDX = (wddxString) => {
    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(wddxString, 'text/xml');

        const parseNode = (node) => {
            if (!node) return null;

            switch (node.nodeName.toLowerCase()) {
                case 'struct': {
                    const obj = {};
                    const vars = node.getElementsByTagName('var');
                    for (let i = 0; i < vars.length; i++) {
                        const varNode = vars[i];
                        if (varNode.parentNode === node) {
                            const name = varNode.getAttribute('name');
                            const valueNode = varNode.children[0];
                            obj[name] = parseNode(valueNode);
                        }
                    }
                    return obj;
                }
                case 'array': {
                    const arr = [];
                    for (let i = 0; i < node.children.length; i++) {
                        arr.push(parseNode(node.children[i]));
                    }
                    return arr;
                }
                case 'string':
                    return node.textContent || '';
                case 'number':
                    return parseFloat(node.textContent) || 0;
                case 'boolean':
                    return node.textContent.toLowerCase() === 'true';
                case 'null':
                    return null;
                default:
                    return node.textContent;
            }
        };

        const dataNode = xmlDoc.querySelector('data > *');
        return dataNode ? parseNode(dataNode) : null;
    } catch (e) {
        console.error('WDDX parse error:', e);
        return null;
    }
};

/**
 * Normalize user data from API response
 * Handles both camelCase, lowercase and UPPERCASE keys from ColdFusion
 */
const normalizeUserData = (userData) => {
    const firstName = userData.firstName || userData.FIRSTNAME || userData.firstname || '';
    const lastName = userData.lastName || userData.LASTNAME || userData.lastname || '';
    const email = userData.email || userData.EMAIL || '';
    const id = userData.id || userData.ID || '';
    const peopleUuid = userData.peopleUuid || userData.PEOPLEUUID || userData.peopleuuid || '';
    const storageKey = userData.storageKey || userData.STORAGEKEY || userData.storagekey || '';
    const role = userData.role || userData.ROLE || '';

    // isAdmin can be 0, 1, true, false - need to check for undefined explicitly
    let isAdmin = 0;
    if (userData.isAdmin !== undefined) isAdmin = userData.isAdmin;
    else if (userData.ISADMIN !== undefined) isAdmin = userData.ISADMIN;
    else if (userData.isadmin !== undefined) isAdmin = userData.isadmin;

    return {
        id,
        email,
        firstName,
        lastName,
        peopleUuid,
        storageKey,
        role,
        isAdmin: isAdmin === 1 || isAdmin === '1' || isAdmin === true
    };
};

/**
 * Save user data to localStorage
 */
const saveUserToStorage = (user, token = null) => {
    if (token) {
        localStorage.setItem('authToken', token);
    }
    localStorage.setItem('userEmail', user.email);
    localStorage.setItem('userName', `${user.firstName} ${user.lastName}`.trim());
    localStorage.setItem('userId', user.id.toString());
    localStorage.setItem('userUuid', user.peopleUuid);
    localStorage.setItem('userRole', user.role);
    localStorage.setItem('cabaza_user', JSON.stringify(user));

    console.log('User saved to localStorage:', {
        id: user.id,
        email: user.email,
        isAdmin: user.isAdmin
    });
};

/**
 * Clear user data from localStorage
 */
const clearUserFromStorage = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userId');
    localStorage.removeItem('userUuid');
    localStorage.removeItem('userRole');
    localStorage.removeItem('cabaza_user');
};

/**
 * Get current user from localStorage
 */
export const getStoredUser = () => {
    try {
        const userJson = localStorage.getItem('cabaza_user');
        if (userJson) {
            return JSON.parse(userJson);
        }
    } catch (e) {
        console.error('Error parsing cabaza_user from localStorage:', e);
    }
    return null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
    const token = localStorage.getItem('authToken');
    const user = getStoredUser();
    return !!(token && user?.id);
};

/**
 * Check if current user is admin
 */
export const isAdmin = () => {
    const user = getStoredUser();
    return user?.isAdmin === true;
};

/**
 * Main authentication hook
 */
export const useAuth = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Login with email and access code
     */
    const login = useCallback(async (email, code) => {
        setLoading(true);
        setError(null);

        try {
            // Send code as-is - backend /peoples/login will hash it with SHA-256
            const response = await fetch(`${API_BASE_URL}/peoples/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password: code }),
            });

            const text = await response.text();
            console.log('Login API raw response:', text);

            if (!text || text.trim() === '') {
                throw new Error('Empty server response');
            }

            let data;
            if (text.trim().startsWith('<')) {
                // Parse WDDX format from ColdFusion
                data = parseWDDX(text);
                if (!data) {
                    throw new Error('Unable to parse WDDX response');
                }
            } else {
                data = JSON.parse(text);
            }

            const success = data.success || data.SUCCESS;
            const message = data.message || data.MESSAGE;

            if (!success) {
                throw new Error(message || 'Invalid credentials');
            }

            // Extract token and user data
            const responseData = data.data || data.DATA || data;
            const token = responseData.token || responseData.TOKEN || data.token || data.TOKEN;
            const userData = responseData.user || responseData.USER || data.user || data.USER || responseData;

            console.log('Login userData:', userData);
            console.log('Login userData keys:', Object.keys(userData));

            // Normalize and save user
            const user = normalizeUserData({ ...userData, email });
            saveUserToStorage(user, token || 'user-authenticated');

            setLoading(false);
            return { success: true, user };

        } catch (err) {
            console.error('Login error:', err);
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    }, []);

    /**
     * Login/register with OAuth (after redirect from Google)
     * Fetches user data from API using the email from OAuth
     */
    const loginWithOAuth = useCallback(async (authToken, authEmail) => {
        setLoading(true);
        setError(null);

        try {
            // Store the token first
            localStorage.setItem('authToken', authToken);
            localStorage.setItem('userEmail', authEmail);

            // Fetch user data from API
            const response = await fetch(`${API_BASE_URL}/peoples?email=${encodeURIComponent(authEmail)}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await response.json();
            const success = result.success || result.SUCCESS;
            const data = result.data || result.DATA || [];

            if (success && data.length > 0) {
                const userData = data[0];
                const user = normalizeUserData({ ...userData, email: authEmail });
                saveUserToStorage(user, authToken);

                setLoading(false);
                return { success: true, user };
            } else {
                throw new Error('User not found');
            }

        } catch (err) {
            console.error('OAuth login error:', err);
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    }, []);

    /**
     * Refresh user data from API (useful to update isAdmin, etc.)
     */
    const refreshUser = useCallback(async () => {
        const currentUser = getStoredUser();
        if (!currentUser?.peopleUuid) {
            return { success: false, error: 'No user logged in' };
        }

        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/peoples/${currentUser.peopleUuid}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await response.json();
            const success = result.success || result.SUCCESS;
            const data = result.data || result.DATA;

            if (success && data) {
                const user = normalizeUserData({ ...data, email: currentUser.email });
                saveUserToStorage(user);

                setLoading(false);
                return { success: true, user };
            } else {
                throw new Error(result.message || result.MESSAGE || 'Failed to refresh user');
            }

        } catch (err) {
            console.error('Refresh user error:', err);
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    }, []);

    /**
     * Logout - clear all user data
     */
    const logout = useCallback(() => {
        clearUserFromStorage();
        // Optionally clear other app data
        localStorage.removeItem('cabaza_user_contacts');
        window.location.href = '/login';
    }, []);

    /**
     * Get current user
     */
    const getUser = useCallback(() => {
        return getStoredUser();
    }, []);

    return {
        login,
        loginWithOAuth,
        refreshUser,
        logout,
        getUser,
        isAuthenticated: isAuthenticated(),
        isAdmin: isAdmin(),
        loading,
        error,
        setError,
    };
};

export default useAuth;
